/**
 * @fileoverview Automatic drafting review panel (MOR-906 §7.3, MOR-1041).
 *
 * "Draft this week" (or the daily scheduled run) asks the backend to
 * collect the practitioner's activity — inside Morgan and, when connected,
 * Outlook calendar, sent mail and Microsoft 365 documents — and propose
 * evidence-linked entries. Every suggestion is visibly AI-labelled, shows
 * its duration basis (measured / scheduled / estimated), its evidence with
 * links, any time overlap, and requires explicit acceptance. Edits, merges,
 * splits and dismissals persist and survive reruns. Per-source coverage of
 * the last run is shown so partial data is never mistaken for a quiet day.
 *
 * Dependencies:
 * - Convex: timesheets.drafting.*
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { Combine, Loader2, RotateCcw, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TimeRoundingMode } from "@cvx/timesheets/timesheetSettings/schema";

import { extractErrorMessage } from "@/utils/errors";
import { Button } from "@/ui/primitives";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays/alert-dialog";
import { DraftCoverageChips } from "./DraftCoverageChips";
import { DraftingSettingsPopover } from "./DraftingSettingsPopover";
import { DraftSuggestionRow } from "./DraftSuggestionRow";

/** ISO `YYYY-MM-DD` rendered as day/month/year. */
function formatDraftDate(isoDate: string): string {
  return format(parseISO(isoDate), "dd/MM/yyyy");
}

function formatDraftRange(startDate: string, endDate: string): string {
  if (startDate === endDate) return formatDraftDate(startDate);
  return `${formatDraftDate(startDate)} to ${formatDraftDate(endDate)}`;
}

export function DraftSuggestionsPanel({
  orgId,
  weekStart,
  weekEnd,
  fmtHours,
  unitMinutes,
  roundingMode,
  jobs,
  categories,
}: {
  orgId: Id<"organizations">;
  weekStart: string;
  weekEnd: string;
  fmtHours: (units: number) => string;
  /** Firm's unit size — the narrative prompt judges words against hours. */
  unitMinutes: number;
  /** MOR-1016 — firm's part-unit rounding direction for accepted units. */
  roundingMode: TimeRoundingMode;
  jobs: { _id: Id<"jobs">; name: string }[];
  categories: { _id: Id<"timeCategories">; name: string }[];
}) {
  const { data: run } = useQuery(
    convexQuery(api.timesheets.drafting.latestRun, { orgId }),
  );
  const { data: suggestions } = useQuery(
    convexQuery(api.timesheets.drafting.listPendingSuggestions, {
      orgId,
      startDate: weekStart,
      endDate: weekEnd,
    }),
  );
  const requestDraft = useMutation(api.timesheets.drafting.requestDraft);
  const retryDraft = useMutation(api.timesheets.drafting.retryDraft);
  const mergeSuggestions = useMutation(
    api.timesheets.drafting.mergeSuggestions,
  );
  const dismissSuggestions = useMutation(
    api.timesheets.drafting.dismissSuggestions,
  );

  const [requesting, setRequesting] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [selected, setSelected] = useState<Set<Id<"timeEntrySuggestions">>>(
    new Set(),
  );

  const isRunning = run?.status === "running";
  const pending = suggestions ?? [];
  const selectedRows = pending.filter((s) => selected.has(s._id));
  const allSelected =
    pending.length > 0 && pending.every((s) => selected.has(s._id));
  const canMerge =
    selectedRows.length >= 2 &&
    selectedRows.every((s) => s.date === selectedRows[0].date);

  const handleDraft = async () => {
    setRequesting(true);
    try {
      await requestDraft({ orgId, startDate: weekStart, endDate: weekEnd });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not start the draft."));
    } finally {
      setRequesting(false);
    }
  };

  const handleRetry = async () => {
    if (!run) return;
    setRequesting(true);
    try {
      await retryDraft({ runId: run._id });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not retry the draft."));
    } finally {
      setRequesting(false);
    }
  };

  const handleMerge = async () => {
    if (!canMerge) return;
    try {
      await mergeSuggestions({ suggestionIds: selectedRows.map((s) => s._id) });
      setSelected(new Set());
      toast.success("Merged into one entry.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not merge the entries."));
    }
  };

  const handleDismissSelected = async () => {
    if (selectedRows.length === 0 || dismissing) return;
    const count = selectedRows.length;
    setDismissing(true);
    try {
      await dismissSuggestions({
        suggestionIds: selectedRows.map((s) => s._id),
      });
      setSelected(new Set());
      toast.success(
        count === 1
          ? "Suggestion dismissed."
          : `${count} suggestions dismissed.`,
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not dismiss the entries."));
    } finally {
      setDismissing(false);
    }
  };

  const setRowSelected = (id: Id<"timeEntrySuggestions">, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <section
      className="overflow-hidden rounded-xl border border-violet-200/60 bg-violet-50/40 dark:border-violet-900/40 dark:bg-violet-950/20"
      aria-label="AI draft suggestions"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Sparkles className="size-4 text-violet-600 dark:text-violet-400" />
            <span className="text-sm font-semibold">AI suggested</span>
            {pending.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {pending.length} suggested{" "}
                {pending.length === 1 ? "entry" : "entries"} to review
              </span>
            )}
            {run?.status === "complete" &&
              pending.length === 0 &&
              run.suggestionCount === 0 && (
                <span className="text-xs text-muted-foreground">
                  No new draftable activity found
                  {run.signalCount === 0 ? " — no activity in range" : ""}.
                </span>
              )}
            {run?.status === "error" && (
              <span className="text-xs text-destructive" role="alert">
                Drafting failed: {run.error ?? "unknown error"}
                {run.retryable ? " — this can be retried." : ""}
              </span>
            )}
          </div>
          {run && run.status !== "running" && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>
                {run.trigger === "scheduled" ? "Daily draft" : "Draft"}{" "}
                {run.completedAt !== undefined
                  ? formatDistanceToNow(run.completedAt, { addSuffix: true })
                  : ""}
                {" · "}
                {formatDraftRange(run.startDate, run.endDate)}
              </span>
              <DraftCoverageChips coverage={run.sourceCoverage} />
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {pending.length > 0 && !allSelected && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8"
              onClick={() => setSelected(new Set(pending.map((s) => s._id)))}
            >
              Select all
            </Button>
          )}
          {selectedRows.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8"
              onClick={() => setSelected(new Set())}
            >
              Clear selection
            </Button>
          )}
          {selectedRows.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-destructive"
                  disabled={dismissing}
                >
                  {dismissing ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <X className="size-4" />
                  )}
                  Dismiss {selectedRows.length}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {selectedRows.length === 1
                      ? "Dismiss this suggestion?"
                      : `Dismiss ${selectedRows.length} suggestions?`}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {selectedRows.length === 1
                      ? "It will be removed from your review and will not be added to your timesheet. The same activity will not be suggested again."
                      : "They will be removed from your review and will not be added to your timesheet. The same activities will not be suggested again."}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => void handleDismissSelected()}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Dismiss
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {canMerge && (
            <Button
              size="sm"
              variant="secondary"
              className="h-8"
              onClick={() => void handleMerge()}
            >
              <Combine className="size-4" />
              Merge {selectedRows.length}
            </Button>
          )}
          <DraftingSettingsPopover orgId={orgId} />
          {run?.status === "error" && run.retryable && (
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              disabled={requesting}
              onClick={() => void handleRetry()}
            >
              <RotateCcw className="size-4" />
              Retry
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-8 border-violet-300 dark:border-violet-800"
            disabled={isRunning || requesting}
            onClick={() => void handleDraft()}
          >
            {isRunning || requesting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Sparkles className="size-4" />
            )}
            {isRunning ? "Drafting…" : "Draft this week"}
          </Button>
        </div>
      </div>

      {pending.length > 0 && (
        <div className="flex flex-col divide-y divide-border/40 border-t border-border/40 bg-background/60">
          {pending.map((s) => (
            <DraftSuggestionRow
              key={s._id}
              suggestion={s}
              orgId={orgId}
              fmtHours={fmtHours}
              unitMinutes={unitMinutes}
              roundingMode={roundingMode}
              jobs={jobs}
              categories={categories}
              selected={selected.has(s._id)}
              onSelectedChange={(on) => setRowSelected(s._id, on)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
